import React from "react";
import {
  Calendar,
  CalendarClock,
  CalendarOff,
  CheckCircle2,
  Filter,
  LayoutGrid,
  Radar,
  Trophy,
  XCircle,
} from "lucide-react";
import AdminFiltersRail, {
  AdminFilterSection,
  AdminNavFilterItem,
} from "../components/AdminFiltersRail";
import {
  LEAGUE_DESK_VIEWS,
  LEAGUE_STATUS_FILTERS,
  SEASON_PHASE_FILTERS,
} from "./constants";

const DESK_ICONS = {
  managed: Trophy,
  candidates: Radar,
};

const STATUS_ICONS = {
  all: LayoutGrid,
  enabled: CheckCircle2,
  disabled: XCircle,
};

const SEASON_ICONS = {
  all: Calendar,
  in_season: CalendarClock,
  no_upcoming: CalendarOff,
  upcoming: Calendar,
  ended: CalendarOff,
};

export default function AdminLeaguesFiltersRail({
  deskMode = "managed",
  onDeskModeChange,
  status,
  seasonPhase,
  summary,
  candidateSummary,
  resultCount,
  onStatusChange,
  onSeasonPhaseChange,
  onClear,
  activeCount,
  compact,
  onClose,
}) {
  const isCandidates = deskMode === "candidates";
  const activeSummary = isCandidates ? candidateSummary : summary;

  const statusCounts = {
    all: activeSummary?.total ?? 0,
    enabled: activeSummary?.enabled ?? 0,
    disabled: activeSummary?.disabled ?? 0,
  };

  const seasonCounts = {
    all: activeSummary?.total ?? 0,
    in_season: activeSummary?.season?.in_season ?? 0,
    no_upcoming: activeSummary?.season?.no_upcoming ?? 0,
    upcoming: activeSummary?.season?.upcoming ?? 0,
    ended: activeSummary?.season?.ended ?? 0,
  };

  const summaryCopy = [
    resultCount != null
      ? `${resultCount} ${isCandidates ? "candidate" : "league"}${resultCount === 1 ? "" : "s"} in view`
      : null,
    activeCount ? `${activeCount} filter${activeCount === 1 ? "" : "s"} on` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <AdminFiltersRail
      title={isCandidates ? "Candidate pool" : "Leagues desk"}
      subtitle={isCandidates ? "Discover · promote · sync" : "Catalog · seasons · fixtures"}
      deskIcon={Trophy}
      compact={compact}
      onClose={onClose}
      activeCount={activeCount}
      onClear={onClear}
      summaryCount={resultCount ?? activeSummary?.total}
      summaryCopy={summaryCopy || undefined}
    >
      <AdminFilterSection label="Desk" labelIcon={Trophy}>
        {LEAGUE_DESK_VIEWS.map((view) => (
          <AdminNavFilterItem
            key={view.id}
            active={deskMode === view.id}
            onClick={() => onDeskModeChange?.(view.id)}
            icon={DESK_ICONS[view.id]}
            label={view.label}
            hint={view.hint}
            count={view.id === "managed" ? summary?.total : candidateSummary?.total}
          />
        ))}
      </AdminFilterSection>

      {!isCandidates ? (
        <AdminFilterSection label="Sync status" labelIcon={Filter}>
          {LEAGUE_STATUS_FILTERS.map((item) => (
            <AdminNavFilterItem
              key={item.id}
              active={status === item.id}
              onClick={() => onStatusChange(item.id)}
              icon={STATUS_ICONS[item.id]}
              label={item.label}
              hint={item.hint}
              count={statusCounts[item.id]}
            />
          ))}
        </AdminFilterSection>
      ) : null}

      <AdminFilterSection label="Season window" labelIcon={Calendar}>
        {SEASON_PHASE_FILTERS.map((item) => (
          <AdminNavFilterItem
            key={item.id}
            active={seasonPhase === item.id}
            onClick={() => onSeasonPhaseChange(item.id)}
            icon={SEASON_ICONS[item.id]}
            label={item.label}
            hint={item.hint}
            count={seasonCounts[item.id]}
            countTone={item.id === "no_upcoming" && seasonCounts.no_upcoming > 0 ? "warn" : undefined}
            urgent={item.id === "no_upcoming" && seasonCounts.no_upcoming > 0}
          />
        ))}
      </AdminFilterSection>
    </AdminFiltersRail>
  );
}
